import { useEffect, useRef } from "react";
import { useAgent } from "agents/react";
import { useAgentChat } from "agents/ai-react";
import { Box, Button, Input, Sheet, Typography } from "@mui/joy";
import { useTranslation } from "../i18n";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import { MemoizedMarkdown } from "./memoized-markdown";
import { Stack } from "@mui/material";
import ListAltIcon from "@mui/icons-material/ListAlt";
import EventIcon from "@mui/icons-material/Event";
import UpdateIcon from "@mui/icons-material/Update";
import BuildIcon from "@mui/icons-material/Build";

export interface AIChatProps {
  conversationId: string;
  accessToken?: string;
}

export default function AIChat({ conversationId, accessToken }: AIChatProps) {
  const t = useTranslation();
  const sheetRef = useRef<HTMLDivElement>(null);

  const agent = useAgent({
    agent: "chat",
    host: "https://qcsku-agent.doronindm.workers.dev",
    query: {
      authorization: `Bearer ${accessToken}`,
      conversationId,
    },
  });

  const {
    messages: agentMessages,
    input: agentInput,
    handleInputChange: handleAgentInputChange,
    handleSubmit: handleAgentSubmit,
    setInput: setAgentInput,
    isLoading,
    stop,
  } = useAgentChat({
    agent,
    maxSteps: 5,
  });

  useEffect(() => {
    if (!sheetRef.current) return;

    const handle = setTimeout(() => {
      sheetRef.current!.scrollTo({
        top: sheetRef.current!.scrollHeight,
        behavior: "smooth",
      });
    }, 20);

    return () => clearTimeout(handle);
  }, [agentMessages]);

  return (
    <>
      {/* Messages */}
      <Sheet
        sx={{
          flex: 1,
          color: "#d4d4d4",
          fontFamily: "monospace",
          overflowY: "auto",
          p: 1,
          borderRadius: 0,
        }}
        ref={sheetRef}
      >
        {agentMessages.length === 0 && (
          <>
            <Typography fontSize={12}>
              {t(
                "Start a conversation with your AI assistant. Try asking about:"
              )}
            </Typography>
            <Stack direction="column" sx={{ mt: 2 }} gap={2}>
              <Button
                size="sm"
                variant="outlined"
                sx={{
                  fontSize: "12px",
                }}
                startDecorator={<ListAltIcon />}
                onClick={() => setAgentInput("View Draft Inspections")}
              >
                {t("View Draft Inspections")}
              </Button>

              <Button
                size="sm"
                sx={{
                  fontSize: "12px",
                }}
                variant="outlined"
                startDecorator={<EventIcon />}
                onClick={() => setAgentInput("Next Week's Inspections")}
              >
                {t("Next Week's Inspections Schedule")}
              </Button>

              <Button
                size="sm"
                sx={{
                  fontSize: "12px",
                }}
                variant="outlined"
                startDecorator={<UpdateIcon />}
                onClick={() =>
                  setAgentInput("Recent In-Progress Inspections Updates")
                }
              >
                {t("Recent In-Progress Inspections Updates")}
              </Button>
            </Stack>
          </>
        )}

        {agentMessages.map((m, index) => {
          const isUser = m.role === "user";
          return (
            <Box key={index} sx={{ mb: 1, whiteSpace: "pre-wrap" }}>
              <Typography
                sx={{
                  color: isUser ? "#4ec9b0" : "#9cdcfe",
                  fontFamily: "monospace",
                  fontSize: "10px",
                }}
              >
                {m.parts?.map((part, i) => {
                  if (part.type === "text") {
                    return (
                      <div key={i}>
                        <MemoizedMarkdown
                          id={`${m.id}-${i}`}
                          content={
                            (isUser ? "$ " : "") +
                            part.text.replace(/^scheduled message: /, "")
                          }
                        />
                      </div>
                    );
                  }

                  if (part.type === "tool-invocation") {
                    return (
                      <Box
                        key={i}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          fontSize: "12px",
                          fontFamily: "monospace",
                          mb: 0.5,
                          color: "#7a7a7a",
                        }}
                      >
                        {/* Gear icon */}
                        <BuildIcon sx={{ fontSize: 14, mr: 1 }} />

                        {/* Tool name + status */}
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            component="span"
                            sx={{
                              fontSize: "12px",
                              fontFamily: "monospace",
                              ...(isLoading
                                ? {
                                    animation: "blink 1s infinite",
                                    opacity: 0.7,
                                    "@keyframes blink": {
                                      "0%, 50%, 100%": { opacity: 0.7 },
                                      "25%, 75%": { opacity: 0.5 },
                                    },
                                  }
                                : {}),
                            }}
                          >
                            {part.toolInvocation.toolName}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  }

                  if (part.type === "reasoning") {
                    return (
                      <Box
                        key={i}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          fontSize: "12px",
                          fontFamily: "monospace",
                          mb: 0.5,
                          color: "#7a7a7a",
                        }}
                      >
                        {/* Gear icon */}
                        <BuildIcon sx={{ fontSize: 14, mr: 1 }} />

                        {/* Tool name + status */}
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            component="span"
                            sx={{
                              fontSize: "12px",
                              fontFamily: "monospace",
                              ...(isLoading
                                ? {
                                    animation: "blink 1s infinite",
                                    opacity: 0.7,
                                    "@keyframes blink": {
                                      "0%, 50%, 100%": { opacity: 0.7 },
                                      "25%, 75%": { opacity: 0.5 },
                                    },
                                  }
                                : {}),
                            }}
                          >
                            {part.reasoning}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  }

                  return null;
                })}
              </Typography>
            </Box>
          );
        })}
      </Sheet>

      {/* Input Area */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          p: 0,
          borderTop: "1px solid #333",
        }}
        gap={2}
      >
        <Input
          startDecorator={
            <Typography sx={{ color: "#4ec9b0", mr: 1 }}>{">"}</Typography>
          }
          variant="plain"
          placeholder="Type here..."
          value={agentInput}
          onChange={handleAgentInputChange}
          onKeyDown={(e) => {
            if (
              e.key === "Enter" &&
              !e.shiftKey &&
              !e.nativeEvent.isComposing
            ) {
              e.preventDefault();
              handleAgentSubmit(e as unknown as React.FormEvent);
            }
          }}
          sx={{
            overlay: "none",
            flex: 1,
            color: "#d4d4d4",
            fontFamily: "monospace",
            fontSize: "12px",
            "& input": { p: 0 },
          }}
        />
        {isLoading ? (
          <Button
            size="sm"
            color="neutral"
            variant="outlined"
            endDecorator={<StopCircleIcon />}
            onClick={stop}
            aria-label="Stop generation"
          >
            {" "}
            {t("Send")}{" "}
          </Button>
        ) : (
          <Button
            endDecorator={<SendRoundedIcon />}
            variant="outlined"
            size="sm"
            color="primary"
            onClick={(e) => {
              handleAgentSubmit(Object.assign(e, { value: agentInput }));
            }}
          >
            {t("Send")}
          </Button>
        )}
      </Box>
    </>
  );
}
